'use client';

import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { openModalEvent } from '@nx-frontend/shared/ui/modal';
import { $app } from '@nx-frontend/shared/store/app';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $orgCoupons } from '@nx-frontend/shared/store/org-coupons';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { $orgRoleState } from '@nx-frontend/shared/store/org-role';
import { $user } from '@nx-frontend/shared/store/user';
import {
  BillingAddonCode,
  OrgInfoSubscriptionType,
  OrganizationPrivilege,
  PaywallPopupPayload,
  PaywallPopupPricingReason,
  PaywallPopupReason,
  PopupType,
} from '@nx-frontend/shared/types';
import { getPaywallTitle, hasMaxSubscriptionPlan } from '@nx-frontend/shared/utils';
import { hasAppsumoBundle } from '@nx-frontend/shared/utils';
import { getOpenAddonPopup, getOpenPaywallPopup, goToAppsumoBundlePurchase } from '@nx-frontend/widgets/popups';

enum AppWindowEventsEnum {
  PAYWALL_SHOW = 'paywall:show',
  AUTOMATION_SHOW = 'automation:show',
}

/**
 * Widget for handling window events
 * @returns
 */
export function AppWindowEvents() {
  const [app, user, orgInfo, currentOrganization, billingInfo, orgCoupons, role] = useUnit([
    $app,
    $user,
    $orgInfo,
    $currentOrganization,
    $billingInfo,
    $orgCoupons,
    $orgRoleState,
  ]);
  const [openModal] = useUnit([openModalEvent]);
  const t = useTranslations();

  useEffect(() => {
    // Handle paywall:show event
    const paywalShow = (event: Event) => {
      const customEvent = event as CustomEvent<{
        reason?: string;
        title?: string;
        coupon?: string;
        checkSubscription?: boolean;
      }>;

      const isMaxSubscriptionPlan = hasMaxSubscriptionPlan(orgInfo, billingInfo, orgCoupons);
      const showAppsumoBundleBanner = hasAppsumoBundle(billingInfo, orgCoupons);
      if (isMaxSubscriptionPlan && showAppsumoBundleBanner) {
        // redirect to appsumo bundle straight away
        goToAppsumoBundlePurchase(currentOrganization.globalId);
      } else {
        let allowUpgrade =
          role.privileges.includes(OrganizationPrivilege.CAN_UPDATE_ORG) &&
          !isMaxSubscriptionPlan &&
          !Object.keys(orgCoupons.couponNumber).length;
        if (customEvent.detail.checkSubscription) {
          allowUpgrade = allowUpgrade && orgInfo.subscriptionType === OrgInfoSubscriptionType.STANDARD;
        }
        if (showAppsumoBundleBanner) {
          allowUpgrade = true;
        }

        if (allowUpgrade) {
          let paywallTitle = getPaywallTitle(orgInfo.subscriptionType, t);
          if (
            orgInfo.subscriptionType === 'team' &&
            orgCoupons.isLoaded &&
            Object.keys(orgCoupons.couponNumber).length
          ) {
            paywallTitle = t('features.paywall.title_boost_client_experience');
          }
          let reason = PaywallPopupReason.WINDOW_EVENT;
          if ('automation' === customEvent.detail.reason) {
            reason = PaywallPopupReason.AUTOMATION;
          }
          const paywallPopupPayload: PaywallPopupPayload = {
            payloadType: PopupType.PAYWALL,
            title: customEvent.detail.title || paywallTitle,
            reason,
            pricingReason: PaywallPopupPricingReason.MENU_UPGRADE_TO_PRO,
            orgId: currentOrganization.globalId,
          };
          getOpenPaywallPopup({ openModal, payload: paywallPopupPayload });
        }
      }
    };

    // Handle automation:show event
    const automationShow = (event: Event) => {
      if (Object.keys(orgCoupons.couponNumber).length) {
        openModal({
          content: PopupType.AUTOMATION_LIMIT_LTD,
          showCloseButton: false,
        });
      } else {
        getOpenAddonPopup({
          openModal,
          payload: {
            payloadType: PopupType.ADDON,
            code: BillingAddonCode.AUTOMATION,
          },
        });
      }
    };

    // Register event handlers
    const handlers: Record<AppWindowEventsEnum, (event: Event) => void> = {
      [AppWindowEventsEnum.PAYWALL_SHOW]: paywalShow,
      [AppWindowEventsEnum.AUTOMATION_SHOW]: automationShow,
    };

    // Add event listeners
    Object.entries(handlers).forEach(([event, handler]) => {
      window.addEventListener(event, handler);
    });

    return () => {
      // Remove event listeners
      Object.entries(handlers).forEach(([event, handler]) => {
        window.removeEventListener(event, handler);
      });
    };
  }, [
    orgInfo.subscriptionType,
    currentOrganization.globalId,
    orgInfo,
    currentOrganization,
    billingInfo,
    orgCoupons,
    role.privileges,
    openModal,
    app.sessionId,
    user.id,
    t,
  ]);
  return null;
}
